import { Menu, Transition } from '@headlessui/react';
import fromUnixTime from 'date-fns/fromUnixTime';
import React from 'react';
import { useHistory } from 'react-router-dom';

import { getFilterScheduler } from '../../api/GET_filterScheduler';
import { getScheduler } from '../../api/GET_scheduler';
import Checkbox from '../../components/Checkbox';
import Header from '../../components/Header';
import Layout from '../../components/Layout';
import ModalConfirmation from '../../components/ModalConfirmation';
import ModalFormXlsx from '../../components/ModalFormXlsx';
import Table from '../../components/Table';
import { AuthContext } from '../../context/authContext';
import { convertDay } from '../../utils/convertDay';
import { dateFormatter } from '../../utils/dateFormatter';
import { _retrieveLocalStorageItem } from '../../utils/localStorage';
import { day, List, status } from '../../utils/staticData';
import { Scheduler } from './Home.type';

const Home = () => {
	const [scheduler, setScheduler] = React.useState<Array<Scheduler>>([]);
	const [newScheduler, setNewScheduler] = React.useState<Array<Scheduler>>([]);
	const [searchScheduler, setSearchScheduler] = React.useState<
		Array<Scheduler>
	>([]);
	const [filterScheduler, setFilterScheduler] = React.useState<
		Array<Scheduler>
	>([]);
	const [isLoading, setIsLoading] = React.useState(true);
	const [isFilterVisible, setIsFilterVisible] = React.useState(false);
	const [isFilterActive, setIsFilterActive] = React.useState(false);
	const [textSearch, setTextSearch] = React.useState('');
	const [isCheckOutletAll, setIsCheckOutletAll] = React.useState(true);
	const [days, setDays] = React.useState<Array<List>>([]);
	const [checkOutlets, setCheckOutlets] = React.useState<Array<string>>([]);
	const [checkDays, setCheckDays] = React.useState<Array<string>>([]);
	const [checkStatuses, setCheckStatuses] = React.useState<List>(null);
	const [statuses, setStatuses] = React.useState<Array<List>>([]);
	const [isModalConfirmationVisible, setIsModalConfirmationVisible] =
		React.useState(false);
	const [isModalXlsxVisible, setIsModalXlsxVisible] = React.useState(false);
	const { logout } = React.useContext(AuthContext);
	const [rotate, setRotate] = React.useState('transform duration-700 ease');
	const history = useHistory();
	const navigateToAddScheduler = () => history.push('/inventory-control/add');

	const toggleModalXlsx = () => setIsModalXlsxVisible(!isModalXlsxVisible);
	const toggleModalConfirmation = () =>
		setIsModalConfirmationVisible(!isModalConfirmationVisible);
	const toggleFilter = () => {
		setIsFilterVisible(!isFilterVisible);
		setRotate(
			isFilterVisible
				? 'transform duration-700 ease'
				: 'transform duration-700 ease rotate-180',
		);
	};

	const handleEdit = React.useCallback(
		(id) => {
			history.push(`/inventory-control/${id}`);
		},
		[history],
	);

	const onSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
		setTextSearch(event.target.value);
		if (event.target.value !== '') {
			const filteredScheduler = newScheduler.filter((item) => {
				return Object.values(item)
					.join('')
					.toLowerCase()
					.includes(event.target.value.toLowerCase());
			});
			setSearchScheduler(filteredScheduler);
		} else {
			setSearchScheduler(newScheduler);
		}
	};

	const cancelButtonRef = React.useRef(null);
	const closeModalBackground = () => undefined;

	const columns = React.useMemo(
		() => [
			{
				Header: 'Nama outlet',
				accessor: 'name',
			},
			{
				Header: 'Tanggal Mulai',
				accessor: 'startDate',
			},
			{
				Header: 'Jadwal Reminder',
				accessor: 'reminder',
			},
			{
				Header: 'Status',
				accessor: 'status',
				Cell: ({ value }) => (
					<div
						className={
							value
								? 'w-16 py-1.5 px-3 bg-positive-lighter rounded-full'
								: 'w-24 py-1.5 px-3 bg-warning-lighter rounded-full'
						}
					>
						<div
							className={
								value
									? 'text-positive-default text-center capitalize'
									: 'text-warning-default text-center capitalize'
							}
						>
							{value ? 'Aktif' : 'Nonaktif'}
						</div>
					</div>
				),
			},
			{
				Header: 'Aksi',
				accessor: 'action',
				Cell: ({ row }) => {
					return (
						<div className="flex justify-center items-center">
							<button
								onClick={() => handleEdit(row.original.idScheduler)}
								className="border-2 border-primary-green w-20 rounded-lg py-3 px-4"
							>
								<div className="text-large text-primary-green text-center font-interSemiBold">
									Edit
								</div>
							</button>
						</div>
					);
				},
			},
		],
		[handleEdit],
	);

	const filterSameObject = (scheduler: Array<Scheduler>) => {
		const reduceScheduler = scheduler.reduce((newScheduler, current) => {
			if (!newScheduler.some((item) => item.idOutlet === current.idOutlet)) {
				newScheduler.push(current);
			}
			return newScheduler;
		}, []);
		return reduceScheduler;
	};

	const fetchScheduler = React.useCallback(async () => {
		const { data } = await getScheduler();
		setScheduler(data);
		const filterScheduler = filterSameObject(data);
		const days = day.map((item) => item.value);
		setCheckOutlets(filterScheduler.map((item) => item.idOutlet.toString()));
		setCheckDays(days);
		setCheckStatuses({ label: 'Semua', value: 'all' });
		setNewScheduler(filterScheduler);
		setSearchScheduler(filterScheduler);
		setIsLoading(false);
	}, []);

	const formatScheduler = (currentScheduler: Array<Scheduler>) => {
		const newScheduler = currentScheduler.map((item) => {
			const startDate = fromUnixTime(item.startDate);
			const startDateFormat = dateFormatter(startDate, 'dd MMM yyyy');
			const reminderDay = convertDay(item.reminderDay);
			return {
				name: item.outletName,
				startDate: startDateFormat,
				reminder: `Setiap ${reminderDay} / ${item.reminderHour} WIB`,
				idScheduler: item.idScheduler,
				status: item.active,
			};
		});

		return newScheduler;
	};

	const checkAccessLevel = React.useCallback(async () => {
		const accessLevel = await _retrieveLocalStorageItem('accessLevel');
		if (accessLevel !== 'InventoryControl') {
			logout();
		}
	}, [logout]);

	const onSuccess = () => {
		fetchScheduler();
		toggleModalConfirmation();
	};

	React.useEffect(() => {
		checkAccessLevel();
		fetchScheduler();
		setStatuses(status);
		setDays(day);
	}, [checkAccessLevel, fetchScheduler]);

	const onClickAll = () => {
		setIsCheckOutletAll(!isCheckOutletAll);
		setCheckOutlets(newScheduler.map((item) => item.idOutlet.toString()));
		if (isCheckOutletAll) {
			setCheckOutlets([]);
		}
	};

	const onClick = (
		event: React.ChangeEvent<HTMLInputElement>,
		type: 'outlet' | 'day',
	) => {
		const { id, checked } = event.target;
		switch (type) {
			case 'outlet':
				setCheckOutlets([...checkOutlets, id]);
				if (!checked) {
					setCheckOutlets(checkOutlets.filter((item) => item !== id));
				}
				break;
			case 'day':
				setCheckDays([...checkDays, id]);
				if (!checked) {
					setCheckDays(checkDays.filter((item) => item !== id));
				}
				break;
		}
	};

	const isValidFilter = () => {
		const filterOutlet = newScheduler.filter((item) => {
			return !~checkOutlets.indexOf(item.idOutlet.toString());
		});
		const filterDay = day.filter((item) => {
			return !~checkDays.indexOf(item.value);
		});
		if (
			filterOutlet.length > 0 ||
			filterDay.length > 0 ||
			checkStatuses.value !== 'all'
		) {
			return false;
		} else {
			return true;
		}
	};

	const onChangeStatus = (event: React.ChangeEvent<HTMLInputElement>) => {
		const checkStatuses = { label: event.target.name, value: event.target.id };
		setCheckStatuses(checkStatuses);
	};

	const outletList =
		searchScheduler.length > 0 ? (
			searchScheduler.map(({ outletName, idOutlet }, index) => {
				const id = idOutlet.toString();
				return (
					<div className="flex items-center mb-4" key={index}>
						<Checkbox
							type="checkbox"
							name={outletName}
							id={id}
							onClick={(event) => onClick(event, 'outlet')}
							isChecked={checkOutlets.includes(id)}
						/>
						<div className="text-large text-gray-gray700 font-interMedium ml-3.5">
							{outletName}
						</div>
					</div>
				);
			})
		) : (
			<div className="h-full flex justify-center items-center">
				<div className="m-auto">
					<div className="text-large text-gray-gray700 font-interRegular">
						Hasil tidak ditemukan
					</div>
				</div>
			</div>
		);

	const dayList = days.map(({ label, value }) => {
		return (
			<div className="flex items-center mb-4" key={value}>
				<Checkbox
					type="checkbox"
					name={label}
					id={value}
					onClick={(event) => onClick(event, 'day')}
					isChecked={checkDays.includes(value)}
				/>
				<div className="text-large text-gray-gray700 font-interMedium ml-3.5">
					{label}
				</div>
			</div>
		);
	});
	const statusList = statuses.map((item) => {
		return (
			<div className="flex items-center mb-4 ml-6.5" key={item.value}>
				<Checkbox
					type="radio"
					name={item.label}
					id={item.value}
					onClick={(event) => onChangeStatus(event)}
					isChecked={item.value === checkStatuses?.value}
				/>
				<div className="text-large text-gray-gray700 font-interMedium ml-3.5">
					{item.label}
				</div>
			</div>
		);
	});

	const onRemoveText = () => {
		setTextSearch('');
		setSearchScheduler(newScheduler);
	};

	const onResetFilter = () => {
		toggleFilter();
		setIsFilterActive(false);
		fetchScheduler();
	};

	const onFilter = async () => {
		const convertCheckOutlets = checkOutlets.map((item) => +item);
		try {
			const { status, data } = await getFilterScheduler(
				convertCheckOutlets,
				checkDays,
				checkStatuses.value,
			);
			if (status === 'success') {
				setFilterScheduler(data);
				setIsFilterActive(true);
				toggleFilter();
			}
		} catch (error) {
			return error;
		}
	};

	const onCancelfilter = () => {
		toggleFilter();
		if (!isFilterActive) {
			fetchScheduler();
		}
	};

	return (
		<div className="flex flex-col min-h-screen overflow-hidden">
			<ModalFormXlsx
				cancelButtonRef={cancelButtonRef}
				showModal={isModalXlsxVisible}
				showModalSuccess={toggleModalConfirmation}
				closeModal={toggleModalXlsx}
				closeModalBackground={closeModalBackground}
			/>
			<ModalConfirmation
				icon="/images/success.svg"
				title="Berhasil Menambahkan Jadwal"
				description="Kamu berhasil menambahkan jadwal baru!"
				onClick={onSuccess}
				cancelButtonRef={cancelButtonRef}
				isShowModal={isModalConfirmationVisible}
				closeModalBackground={closeModalBackground}
				variantButton="SINGLE"
				textCenter="Oke, Mengerti"
			/>
			<Header title="Yummy StockTake System" />
			<Layout>
				<section className="relative laptop:pt-32 tablet:pt-32 mobile:pt-28 pb-14">
					<div className="flex justify-between mb-6 laptop:mx-36 tablet:mx-10 mobile:mx-4">
						<div className="text-gray-gray700 laptop:text-extraLarge2 tablet:text-extraLarge2 mobile:text-extraLarge font-interMedium mb-6">
							Konfigurasi Brand dan Outlet
						</div>
						<div className="flex">
							<a
								className="flex items-center p-3 text-center rounded-lg
               bg-transparent border-2 border-primary-green font-interSemiBold text-white focus:outline-none focus:ring-transparent focus:ring-primary-green mr-4"
								href={`${process.env.REACT_APP_BASE_URL}/export-scheduler`}
							>
								<div className="text-primary-green text-large font-interSemiBold mr-5 ">
									Ekspor Data
								</div>
								<img src="/images/download.svg" className="w-6 h-6" />
							</a>
							<Menu as="div" className="relative inline-block">
								<Menu.Button
									className="flex items-center p-4 text-center rounded-lg
               bg-primary-green font-interSemiBold text-white focus:outline-none focus:ring-transparent focus:ring-primary-green"
								>
									<div className="text-white text-large font-interSemiBold mr-5 ">
										Tambahkan Jadwal
									</div>
									<img src="images/plus.svg" className="w-6 h-6" />
								</Menu.Button>
								<Transition
									as={React.Fragment}
									enter="transition ease-out duration-100"
									enterFrom="transform opacity-0 scale-95"
									enterTo="transform opacity-100 scale-100"
									leave="transition ease-in duration-75"
									leaveFrom="transform opacity-100 scale-100"
									leaveTo="transform opacity-0 scale-95"
								>
									<Menu.Items className="absolute w-97 right-0 mt-2.5 p-4 hover:p-4 origin-center bg-white rounded-xl shadow-lg focus:outline-none">
										<div className="px-1 py-1">
											<Menu.Item>
												{() => (
													<div>
														<div
															onClick={toggleModalXlsx}
															className="flex justify-center items-center cursor-pointer hover:bg-gray-gray200 hover:rounded-xl hover:p-2 mb-4"
														>
															<img src="/images/tnc.svg" className="mr-5" />
															<div>
																<div className="text-large text-gray-gray700 font-interMedium mb-2">
																	Import Excel
																</div>
																<div className="text-large text-gray-gray500 font-interRegular">
																	Gunakan template format XLSX untuk mengupload
																	data secara instan
																</div>
															</div>
														</div>
														<div
															onClick={navigateToAddScheduler}
															className="flex justify-center items-center cursor-pointer hover:bg-gray-gray200 hover:rounded-xl hover:p-2"
														>
															<img src="/images/pencil.svg" className="mr-5" />
															<div>
																<div className="text-large text-gray-gray700 font-interMedium mb-2">
																	Tambahkan Manual
																</div>
																<div className="text-large text-gray-gray500 font-interRegular">
																	Isi informasi jadwal menggunakan form yang
																	tersedia
																</div>
															</div>
														</div>
													</div>
												)}
											</Menu.Item>
										</div>
									</Menu.Items>
								</Transition>
							</Menu>
						</div>
					</div>
					<div className="bg-white rounded-xl shadow-default p-6 mb-6 laptop:mx-36 tablet:mx-10 mobile:mx-4">
						{scheduler.length > 0 && !isLoading ? (
							<>
								<div
									onClick={toggleFilter}
									className="w-80 h-15 relative flex justify-between items-center border border-gray-gray400 rounded-xl p-4 mb-6 cursor-pointer"
								>
									<div className="text-large text-gray-gray700 font-interMedium">
										Filter
										{isFilterActive && (
											<span className="text-large text-gray-gray700 font-interMedium">
												:
												<span className="text-large text-primary-green font-interRegular ml-1">
													Aktif
												</span>
											</span>
										)}
									</div>
									<div>
										<img
											src="/images/chevronDown.svg"
											alt="Chevron icon"
											className={`${rotate}`}
										/>
									</div>
								</div>
								{isFilterVisible && (
									<div className="w-120 h-103 absolute bg-white shadow-xs rounded-xl p-4 -mt-3.5">
										<div className="flex">
											<div>
												<div className="text-extraLarge text-gray-gray700 font-interMedium mb-4">
													Nama Outlet
												</div>
												<div className="w-80 h-80 border-r border-gray-gray400">
													<div className="relative mr-4 mb-4">
														<input
															value={textSearch}
															onChange={onSearch}
															placeholder="Cari nama outlet"
															className="w-full flex justify-center items-center bg-white border border-gray-gray500 rounded-xl laptop:py-3 tablet:py-3 mobile:py-2 px-10 border focus:border-2 focus:border-gray-gray700 text-gray-gray700 laptop:text-large tablet:text-large mobile:text-regular outline-none font-interRegular placeholder-gray-gray400"
														/>
														{textSearch ? (
															<img
																src="/images/searchBlack.svg"
																className="w-6 h-6 absolute mr-3 laptop:-mt-10 tablet:-mt-9 mobile:-mt-7 ml-3"
															/>
														) : (
															<img
																src="/images/search.svg"
																className="absolute mr-3 laptop:-mt-10 tablet:-mt-9 mobile:-mt-7 ml-3"
															/>
														)}
														{textSearch && (
															<div
																onClick={onRemoveText}
																className="w-full absolute laptop:right-4 laptop:-mt-9 tablet:right-4 tablet:-mt-9 mobile:-ml-14 mobile:-mt-8 flex justify-end items-end"
															>
																<img src="images/cross.svg" />
															</div>
														)}
													</div>
													<div className="w-74.5 h-52 overflow-y-auto ml-3">
														{!textSearch && (
															<div className="flex items-center mb-4">
																<Checkbox
																	type="checkbox"
																	name="selectAll"
																	id="selectAll"
																	onClick={onClickAll}
																	isChecked={isCheckOutletAll}
																/>
																<div className="text-large text-gray-gray700 font-interMedium ml-3.5">
																	Pilih Semua
																</div>
															</div>
														)}
														{outletList}
													</div>
												</div>
											</div>
											<div>
												<div className="text-extraLarge text-gray-gray700 font-interMedium mb-4 ml-4">
													Hari Jadwal
												</div>
												<div className="w-55 h-80 border-r border-gray-gray400 ml-6.5">
													{dayList}
												</div>
											</div>
											<div>
												<div className="text-extraLarge text-gray-gray700 font-interMedium mb-4 ml-4">
													Status Scheduler
												</div>
												{statusList}
											</div>
										</div>
										<div className="flex bg-white shadow-lxs justify-between -mt-6.5 relative py-4">
											<div
												onClick={onResetFilter}
												className="flex justify-center items-center cursor-pointer"
											>
												<img src="/images/refresh.svg" />
												<div className="text-large text-gray-gray700 font-interMedium ml-4">
													Reset Filter
												</div>
											</div>
											<div className="flex w-92 space-x-4">
												<button
													type="button"
													className="w-1/2 h-13 px-4 text-center border-2 border-primary-green bg-white rounded-lg focus:outline-none focus:ring-transparent"
													onClick={onCancelfilter}
												>
													<div className="laptop:text-large tablet:text-large mobile:text-regular text-primary-green font-interSemiBold">
														Batal
													</div>
												</button>
												<button
													disabled={isValidFilter()}
													type="button"
													className={
														isValidFilter()
															? 'w-1/2 h-13 px-4 flex justify-center items-center text-center rounded-lg bg-gray-gray300 focus:outline-none focus:ring-transparent focus:ring-gray-gray300'
															: 'w-1/2 h-13 px-4 flex justify-center items-center text-center rounded-lg bg-primary-green focus:outline-none focus:ring-transparent focus:ring-primary-green'
													}
													onClick={onFilter}
												>
													<div
														className={
															isValidFilter()
																? 'laptop:text-large tablet:text-large mobile:text-regular text-gray-gray500 font-interSemiBold'
																: 'laptop:text-large tablet:text-large mobile:text-regular text-white font-interSemiBold'
														}
													>
														Terapkan
													</div>
												</button>
											</div>
										</div>
									</div>
								)}
								<Table
									isFilter
									columns={columns}
									data={formatScheduler(
										isFilterActive ? filterScheduler : scheduler,
									)}
									pageSize={10}
								/>
							</>
						) : scheduler.length === 0 && !isLoading ? (
							<div className="border border-gray-gray300 rounded-xl flex justify-center py-8">
								<div className="w-80 text-extraLarge2 text-gray-gray700 text-center font-interMedium">
									Belum ada data ditampilkan untuk saat ini
								</div>
							</div>
						) : (
							<div className="border border-gray-gray300 rounded-xl flex justify-center py-8">
								<div className="w-80 text-extraLarge2 text-gray-gray700 text-center font-interMedium">
									Sedang memuat data...
								</div>
							</div>
						)}
					</div>
				</section>
			</Layout>
		</div>
	);
};

export default Home;
